import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Compass, Lock, ArrowRight, CheckCircle2, Loader2, Sparkles, Activity, CircleAlert, CircleX, Flame, BarChart3, Download } from "lucide-react";

const STEP_LABELS = {
  business_basics: "Business basics",
  goals: "Goals",
  offers: "Offers",
  audience_segments: "Audience",
  channels: "Channels",
  brand_voice: "Brand voice",
  assets: "Assets",
  constraints: "Constraints",
};

export default function StrategyHome() {
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [profile, setProfile] = useState(null);
  const [today, setToday] = useState(null); // {task, date, health}
  const [consistency, setConsistency] = useState(null); // Sprint 4.6

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const acc = await api.get("/strategy/access");
      setAccess(acc.data);
      if (acc.data.has_access) {
        const p = await api.get("/strategy/profile");
        setProfile(p.data.profile);
        if (p.data.profile?.approval_status === "strategy_approved") {
          try {
            const t = await api.get("/strategy/calendar/today");
            setToday(t.data);
          } catch (e) { console.debug("[strategy-home] calendar/today not ready:", e?.response?.status); }
          try {
            const c = await api.get("/strategy/consistency");
            setConsistency(c.data);
          } catch (e) { console.debug("[strategy-home] consistency not accessible:", e?.response?.status); }
        }
      }
    } catch (e) { console.debug("[strategy-home] profile load failed, showing empty state:", e?.response?.status); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="p-10 flex items-center gap-2 text-[#64748B]" data-testid="strategy-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading your strategy…
    </div>
  );

  // Locked state
  if (!access?.has_access) {
    return (
      <div data-testid="strategy-locked" className="max-w-3xl px-5 sm:px-6 lg:px-10 py-10">
        <div className="dms-label mb-3 inline-flex items-center gap-2">
          <Lock className="w-3 h-3" /> Premium feature
        </div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342]">DMS 90-Day Strategy Operator</h1>
        <p className="mt-3 text-[#475569] text-[15px] max-w-2xl">
          Stop creating random content. Build your 90-day dive marketing strategy around your business goals, offers, audience and active channels — then get daily reminders telling you exactly what to post, where, and why.
        </p>

        <div className="mt-8 dms-card p-6 border-2 border-[#4FD1C5]/40">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-[#0A2342] text-[#4FD1C5] flex items-center justify-center">
              <Compass className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="font-heading font-bold text-[#0A2342] text-[17px]">Included on Business Unlimited</div>
              <div className="mt-1 text-[13.5px] text-[#64748B]">
                You&apos;re currently on <b>{access?.plan_name || "the free trial"}</b>. Upgrade to unlock the Strategy Operator, or contact your admin to enable it on your workspace.
              </div>
              <Link to="/billing" data-testid="strategy-upgrade-cta" className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#0A2342] text-white px-5 py-2.5 text-[13.5px] font-semibold hover:bg-[#152F5A]">
                See plans & upgrade <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            ["Build your 90-day plan", "Map offers, audience, channels & pillars"],
            ["Prepare content weekly", "Batch-create everything for the week in one sitting"],
            ["Daily reminders", "Never miss a post — reminders even when content is pre-approved"],
          ].map(([h, s]) => (
            <div key={h} className="dms-card p-4">
              <Sparkles className="w-4 h-4 text-[#4FD1C5]" />
              <div className="mt-2 font-heading font-bold text-[#0A2342] text-[14px]">{h}</div>
              <div className="text-[12.5px] text-[#64748B] mt-1">{s}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Unlocked — onboarding not complete
  const done = new Set(profile?.onboarding_completed_sections || []);
  const totalSteps = Object.keys(STEP_LABELS).length;
  const pct = Math.round((done.size / totalSteps) * 100);
  const onboardingDone = !!profile?.onboarding_completed_at;

  return (
    <div data-testid="strategy-home" className="max-w-4xl px-5 sm:px-6 lg:px-10 py-8 sm:py-10">
      <div className="dms-label mb-3">DMS Premium</div>
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A2342]">90-Day Strategy Operator</h1>
      <p className="mt-2 text-[#475569] text-[14.5px] max-w-2xl">
        Workspace: <b className="text-[#0A2342]">{access.workspace_name}</b>
      </p>

      {!onboardingDone && (
        <div className="mt-6 dms-card p-6" data-testid="strategy-onboarding-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-heading font-bold text-[#0A2342] text-[17px]">Complete your strategy onboarding</div>
              <div className="text-[13px] text-[#64748B] mt-1">
                {done.size} of {totalSteps} sections done · autosaves as you go
              </div>
              <Link
                to="/downloads/strategy-operator-guide"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="strategy-guide-link"
                className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#1D4E89] hover:text-[#0A2342]"
              >
                How this tool works <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <Link
              to="/strategy/onboarding"
              data-testid="strategy-onboarding-cta"
              className="inline-flex items-center gap-2 rounded-md bg-[#0A2342] text-white px-5 py-2.5 text-[13.5px] font-semibold hover:bg-[#152F5A]"
            >
              {done.size === 0 ? "Start onboarding" : "Continue onboarding"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-4 h-1.5 rounded-full bg-[#E2E8F0] overflow-hidden">
            <div className="h-full bg-[#4FD1C5] transition-all" style={{ width: `${pct}%` }} />
          </div>
          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(STEP_LABELS).map(([k, label]) => (
              <li key={k} className={`text-[13px] flex items-center gap-2 ${done.has(k) ? "text-[#0A2342]" : "text-[#94A3B8]"}`}>
                <CheckCircle2 className={`w-3.5 h-3.5 ${done.has(k) ? "text-[#4FD1C5]" : "text-[#CBD5E1]"}`} />
                {label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {onboardingDone && (
        <div className="mt-6 space-y-4" data-testid="strategy-ready-card">
          <StrategyStatusCard profile={profile} today={today} />
          {profile?.approval_status === "strategy_approved" && (
            <>
              <TodayAndCalendarCards today={today} />
              {consistency && <ConsistencyCard consistency={consistency} />}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link
                  to="/strategy/analytics"
                  data-testid="home-analytics-link"
                  className="dms-card p-4 flex items-center gap-3 hover:border-[#1D4E89] hover:shadow-sm transition"
                >
                  <div className="w-10 h-10 rounded-md bg-[#F0FDFA] text-[#0A2342] flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-[#0A2342] text-[14.5px]">Log weekly analytics</div>
                    <div className="text-[12.5px] text-[#64748B]">Followers, reach, engagement, outcomes per channel.</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#1D4E89] flex-shrink-0" />
                </Link>
                <Link
                  to="/strategy/export"
                  data-testid="home-export-link"
                  className="dms-card p-4 flex items-center gap-3 hover:border-[#1D4E89] hover:shadow-sm transition"
                >
                  <div className="w-10 h-10 rounded-md bg-[#F0FDFA] text-[#0A2342] flex items-center justify-center flex-shrink-0">
                    <Download className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-[#0A2342] text-[14.5px]">Export centre</div>
                    <div className="text-[12.5px] text-[#64748B]">Foundation PDF · Calendar CSV / ICS.</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#1D4E89] flex-shrink-0" />
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


/* ----- Sub-components: status card + today/calendar cards ----- */

function StrategyStatusCard({ profile, today }) {
  const isApproved = profile?.approval_status === "strategy_approved";
  const hasCalendar = !!today?.health?.days_ahead;
  return (
    <div className="dms-card p-6">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[#4FD1C5]/20 text-[#0A2342] flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-bold text-[#0A2342] text-[16px]">
              {isApproved && hasCalendar
                ? "Your 90-day calendar is live"
                : isApproved
                  ? "Foundation approved — generate your calendar"
                  : profile?.foundation
                    ? "Strategy foundation drafted — review it"
                    : "Onboarding complete — generate your foundation"}
            </div>
            <div className="text-[13px] text-[#64748B] mt-0.5">
              {isApproved && hasCalendar
                ? "Open the calendar for Today, This Week and 90-Day views. Rolling calendar health lives at the top."
                : isApproved
                  ? "Turn your pillars, buckets and campaign arcs into 90 concrete days. Costs 5 credits, takes ~1–2 minutes."
                  : profile?.foundation
                    ? "Review pillars, buckets, channel roles and campaign arcs. Ask for changes in plain English."
                    : "DMS will read your onboarding and produce pillars, buckets, channel roles and weekly themes."}
            </div>
          </div>
        </div>
        <Link
          to={isApproved ? "/strategy/calendar" : "/strategy/approve"}
          data-testid={isApproved ? "strategy-open-calendar" : "strategy-open-approval"}
          className="inline-flex items-center gap-2 rounded-md bg-[#0A2342] text-white px-5 py-2.5 text-[13.5px] font-semibold hover:bg-[#152F5A]"
        >
          {isApproved && hasCalendar
            ? "Open calendar"
            : isApproved
              ? "Generate calendar"
              : profile?.foundation
                ? "Review & approve"
                : "Generate strategy"} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}


const HEALTH_LABEL = {
  healthy: { label: "Healthy", cls: "text-emerald-700", Icon: CheckCircle2 },
  attention: { label: "Attention soon", cls: "text-amber-700", Icon: Activity },
  action: { label: "Action needed", cls: "text-orange-700", Icon: CircleAlert },
  critical: { label: "Critical", cls: "text-red-700", Icon: CircleX },
};

function TodayAndCalendarCards({ today }) {
  const task = today?.task;
  const health = today?.health;
  const hasCalendar = !!health?.start_date; // meta.start_date only exists after calendar_meta was persisted
  const hlt = hasCalendar && health ? HEALTH_LABEL[health.status] : null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Today's task card */}
      <div className="md:col-span-2 dms-card p-5" data-testid="home-today-card">
        <div className="dms-label mb-1">Today · {today?.date || "—"}</div>
        {!task && (
          <div className="text-[13.5px] text-[#64748B]">
            No task for today. Generate a calendar or check <Link to="/strategy/calendar" className="text-[#1D4E89] hover:underline">the calendar</Link>.
          </div>
        )}
        {task?.status === "rest" && (
          <div className="text-[15px] font-semibold text-[#0A2342]">🌊 Rest day — take it or batch-record for tomorrow.</div>
        )}
        {task && task.status !== "rest" && (
          <>
            <div className="text-[12px] text-[#1D4E89] font-semibold">{task.channel} · {task.format}</div>
            <div className="text-[16px] font-heading font-bold text-[#0A2342] mt-1 leading-snug">{task.hook}</div>
            <div className="text-[12.5px] text-[#64748B] mt-1">{task.angle}</div>
            <Link
              to="/strategy/calendar"
              data-testid="home-today-open"
              className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#0A2342] hover:underline"
            >
              Open in calendar <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </>
        )}
      </div>

      {/* Calendar health card */}
      <div className="dms-card p-5" data-testid="home-health-card">
        <div className="dms-label mb-1">Calendar health</div>
        {!hasCalendar && (
          <div className="text-[13.5px] text-[#64748B]">
            No calendar yet. Generate one to see rolling health.
            <Link to="/strategy/calendar" className="block mt-2 text-[12.5px] font-semibold text-[#1D4E89] hover:underline">
              Generate calendar →
            </Link>
          </div>
        )}
        {hasCalendar && hlt && (
          <>
            <div className={`inline-flex items-center gap-1.5 text-[13px] font-bold ${hlt.cls}`}>
              <hlt.Icon className="w-4 h-4" /> {hlt.label}
            </div>
            <div className="mt-1 text-[13px] text-[#0A2342]">
              <b>{health.days_ahead}</b> planned days ahead
            </div>
            <Link
              to="/strategy/calendar"
              className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#1D4E89] hover:underline"
            >
              View calendar <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}


function ConsistencyCard({ consistency }) {
  const { done, planned, completion_pct, streak, week_start, week_end } = consistency;
  const hasData = planned > 0 || streak > 0;
  const barColor = completion_pct >= 75 ? "bg-emerald-500" : completion_pct >= 50 ? "bg-[#4FD1C5]" : "bg-orange-400";
  return (
    <div className="dms-card p-5" data-testid="home-consistency-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <div>
          <div className="dms-label">This week · consistency</div>
          <div className="text-[12px] text-[#64748B] mt-0.5">
            {week_start && week_end ? `${week_start} → ${week_end}` : "—"}
          </div>
        </div>
        <div className="text-[11px] text-[#94A3B8]">
          Sunday digest arrives Sunday morning.
        </div>
      </div>

      {!hasData && (
        <div className="text-[13px] text-[#64748B]">
          No completed posts this week yet. Mark a task done or open the calendar to start the streak.
        </div>
      )}

      {hasData && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <StatTile
              value={planned > 0 ? `${done}/${planned}` : "—"}
              label="Done"
              accent="text-[#0A2342]"
              testId="stat-done"
            />
            <StatTile
              value={planned > 0 ? `${completion_pct}%` : "—"}
              label="Completion"
              accent={completion_pct >= 60 ? "text-emerald-700" : "text-orange-700"}
              testId="stat-completion"
            />
            <StatTile
              value={
                <span className="inline-flex items-baseline gap-1">
                  {streak}
                  {streak >= 3 && <Flame className="w-4 h-4 text-red-600 self-center" />}
                </span>
              }
              label="Day streak"
              accent={streak >= 3 ? "text-red-700" : "text-[#0A2342]"}
              testId="stat-streak"
            />
          </div>

          {planned > 0 && (
            <div className="mt-4">
              <div className="h-1.5 w-full bg-[#F1F5F9] rounded-full overflow-hidden">
                <div
                  data-testid="consistency-progress-bar"
                  className={`h-full ${barColor} transition-all`}
                  style={{ width: `${Math.min(100, completion_pct)}%` }}
                />
              </div>
            </div>
          )}

          <div className="mt-4 text-[12.5px] text-[#475569]">
            {completion_pct >= 90 && "🔥 You crushed the week."}
            {completion_pct >= 60 && completion_pct < 90 && "Solid week — keep the streak alive."}
            {completion_pct > 0 && completion_pct < 60 && "Every posted day is one more than nothing."}
            {planned > 0 && completion_pct === 0 && "Fresh start Monday — pick one task and start."}
          </div>
        </>
      )}
    </div>
  );
}


function StatTile({ value, label, accent = "text-[#0A2342]", testId }) {
  return (
    <div className="rounded-md border border-[#E2E8F0] bg-[#F8FAFC] p-3 text-center" data-testid={testId}>
      <div className={`font-heading text-[26px] font-bold leading-none ${accent}`}>
        {value}
      </div>
      <div className="mt-1.5 text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#64748B]">
        {label}
      </div>
    </div>
  );
}

